import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toast';
import { RtModal } from '../ui/RtModal';
import '../../styles/rt2-link-suites.css';

const LinkTestSuitesModal = ({
  isOpen,
  onClose,
  testPlan,
  projectId,
  onSuccess,
  mode = 'link',
}) => {
  const [testSuites, setTestSuites] = useState([]);
  const [selectedSuites, setSelectedSuites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !projectId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setSearch('');
      try {
        const response = await axiosInstance.get(
          `/test_suite/list/${projectId}?page=1&page_size=100`
        );
        const suites = response.test_suites || [];

        if (mode === 'unlink') {
          const linkedSuiteIds = (testPlan?.testSuites || []).map((suite) =>
            typeof suite === 'object' ? suite.suite_id : suite
          );
          if (linkedSuiteIds.length === 0) {
            toast.warning('This test plan has no linked test suites to unlink');
            onClose();
            return;
          }
          if (!cancelled) {
            setTestSuites(suites.filter((s) => linkedSuiteIds.includes(s.suite_id)));
            setSelectedSuites([]);
          }
        } else {
          const alreadyLinked = new Set(
            (testPlan?.testSuites || []).map((suite) =>
              typeof suite === 'object' ? suite.suite_id : suite
            )
          );
          if (!cancelled) {
            setTestSuites(suites);
            setSelectedSuites([...alreadyLinked]);
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Failed to load test suites');
          toast.error('Failed to load test suites');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return testSuites;
    return testSuites.filter((s) =>
      String(s.suite_name || '').toLowerCase().includes(q)
    );
  }, [testSuites, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((s) => selectedSuites.includes(s.suite_id));

  const toggleSuite = (suiteId) => {
    setSelectedSuites((prev) =>
      prev.includes(suiteId) ? prev.filter((id) => id !== suiteId) : [...prev, suiteId]
    );
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const remove = new Set(filtered.map((s) => s.suite_id));
      setSelectedSuites((prev) => prev.filter((id) => !remove.has(id)));
    } else {
      setSelectedSuites((prev) => {
        const next = new Set(prev);
        filtered.forEach((s) => next.add(s.suite_id));
        return [...next];
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedSuites.length === 0) return;

    setSubmitting(true);
    try {
      const endpoint =
        mode === 'link'
          ? `/test_plan/assign_suite_to_plan?test_plan_id=${testPlan.test_plan_id}`
          : `/test_plan/unlink_suite/${testPlan.test_plan_id}`;
      const payload = { suite_ids: selectedSuites };

      const response = await axiosInstance.post(endpoint, payload);
      if (
        response.message === 'Suites assigned to test plan successfully' ||
        response.status_code === 200 ||
        response.status === 200 ||
        mode === 'unlink'
      ) {
        toast.success(
          mode === 'link'
            ? `${selectedSuites.length} suite(s) linked`
            : `${selectedSuites.length} suite(s) unlinked`
        );
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(`Failed: ${err.response.data.detail || 'Bad request'}`);
      } else if (err.response?.status === 422) {
        const details = err.response.data.detail;
        toast.error(
          Array.isArray(details)
            ? details.map((d) => d.msg).join(', ')
            : String(details || 'Validation error')
        );
      } else {
        toast.error(
          err.response?.data?.message ||
            (mode === 'link' ? 'Failed to link test suites' : 'Failed to unlink test suites')
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'link' ? 'Link Test Suites' : 'Unlink Test Suites';
  const subtitle = testPlan?.name
    ? `Choose suites for “${testPlan.name}”`
    : 'Choose suites for this plan';

  return (
    <RtModal
      open={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="lg"
      closeDisabled={submitting}
      className="rt-link-suites-rtmodal"
      bodyClassName="rt-link-suites-rtmodal__body"
      footer={
        <>
          <button
            type="button"
            className="rt-btn rt-btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="rt-link-suites-form"
            className={`rt-btn ${mode === 'unlink' ? 'rt-btn--danger' : 'rt-btn--primary'}`}
            disabled={submitting || selectedSuites.length === 0}
          >
            {submitting
              ? mode === 'link'
                ? 'Linking…'
                : 'Unlinking…'
              : mode === 'link'
                ? `Link ${selectedSuites.length || ''} suite${selectedSuites.length === 1 ? '' : 's'}`.trim()
                : `Unlink ${selectedSuites.length || ''} suite${selectedSuites.length === 1 ? '' : 's'}`.trim()}
          </button>
        </>
      }
    >
      <form id="rt-link-suites-form" className="rt-link-suites-form" onSubmit={handleSubmit}>
        {mode === 'unlink' && (
          <div className="rt-link-suites-banner" role="note">
            Unlinking removes the association only — suites are not deleted.
          </div>
        )}

        <div className="rt-link-suites-toolbar">
          <input
            type="search"
            className="rt-link-suites-search"
            placeholder="Search suites…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search suites"
          />
          <span className="rt-link-suites-count">
            {selectedSuites.length} selected
            {testSuites.length ? ` · ${filtered.length} shown` : ''}
          </span>
          {mode === 'link' && filtered.length > 0 && (
            <button
              type="button"
              className="rt-btn rt-btn--secondary rt-btn--sm"
              onClick={toggleSelectAllFiltered}
            >
              {allFilteredSelected ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>

        {loading ? (
          <div className="rt-link-suites-state">Loading suites…</div>
        ) : error ? (
          <div className="rt-link-suites-state rt-link-suites-state--error">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="rt-link-suites-state">
            {search.trim()
              ? 'No suites match your search'
              : mode === 'link'
                ? 'No test suites available'
                : 'No linked suites'}
          </div>
        ) : (
          <ul className="rt-link-suites-list">
            {filtered.map((suite) => {
              const checked = selectedSuites.includes(suite.suite_id);
              return (
                <li key={suite.suite_id}>
                  <label className={`rt-link-suites-item${checked ? ' is-selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSuite(suite.suite_id)}
                    />
                    <span className="rt-link-suites-item__name">{suite.suite_name}</span>
                    <span className="rt-link-suites-item__id">#{suite.suite_id}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </form>
    </RtModal>
  );
};

export default LinkTestSuitesModal;
